
import { supabase } from "@/integrations/supabase/client";

export interface Task {
  id: string;
  status: string;
  created_at: string;
  result?: any;
  slug?: string;
  version?: string;
}

export const createJob = async (jobSpec: any) => {
  try {
    // Extract slug and job type from the job spec
    const slug = jobSpec.data?.slug;
    const jobType = jobSpec.type;
    
    if (!slug || !jobType) {
      console.error('Missing required job parameters: slug or job type');
      return { data: null, error: new Error('Missing required job parameters: slug or job type') };
    }
    
    console.log(`Creating job for ${slug} of type ${jobType} in slug_jobs table`);
    
    // Check for existing job of this type for this slug in slug_jobs table
    const { data: existingJob, error: checkError } = await supabase
      .from('slug_jobs')
      .select('*')
      .eq('slug', slug)
      .eq('job_type', jobType)
      .maybeSingle();
      
    if (!checkError && existingJob) {
      console.log(`Found existing job for ${slug} of type ${jobType}, updating it`);
      // Update the existing job instead of creating a new one
      const { data: updatedJob, error: updateError } = await supabase
        .from('slug_jobs')
        .update({
          status: 'pending',
          created_at: new Date().toISOString(),
          timestamp: new Date().toISOString()
        })
        .eq('id', existingJob.id)
        .select();
        
      if (updateError) {
        console.error('Error updating existing job:', updateError);
        throw updateError;
      }
      
      console.log('Successfully updated job:', updatedJob);
      return { data: updatedJob, error: null };
    }
    
    // Create a new job in the slug_jobs table
    const { data: newJob, error: insertError } = await supabase
      .from('slug_jobs')
      .insert({
        slug: slug,
        job_type: jobType,
        status: 'pending',
        created_at: new Date().toISOString(),
        timestamp: new Date().toISOString()
      })
      .select();
      
    if (insertError) {
      console.error('Error creating job in slug_jobs table:', insertError);
      throw insertError;
    }
    
    console.log('Successfully created new job:', newJob);
    return { data: newJob, error: null };
  } catch (error) {
    console.error('Error creating job:', error);
    return { data: null, error };
  }
};

export const getJobs = async () => {
  try {
    // Always query the slug_jobs table directly
    return await getJobsDirectQuery();
  } catch (error) {
    console.error('Error in getJobs:', error);
    return { data: null, error };
  }
};

// Function to query the slug_jobs table directly
const getJobsDirectQuery = async () => {
  try {
    console.log('Fetching jobs directly from slug_jobs table');
    const { data, error } = await supabase
      .from('slug_jobs')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching jobs from slug_jobs table:', error);
      return { data: null, error };
    }
    
    // Transform the data to match the expected format
    const transformedJobs = data.map(job => ({
      id: job.id,
      spec: {
        type: job.job_type || 'unknown',
        data: {
          slug: job.slug
        }
      },
      status: job.status,
      result: job.result,
      created_at: job.created_at,
      timestamp: job.timestamp,
      last_successful_timestamp: job.last_successful_timestamp,
      tasks: []
    }));
    
    console.log('Successfully fetched jobs from slug_jobs table:', transformedJobs.length);
    return { data: transformedJobs, error: null };
  } catch (error) {
    console.error('Error in getJobsDirectQuery:', error);
    return { data: null, error };
  }
};

export const deleteJob = async (jobId: string) => {
  try {
    console.log(`Deleting job with ID ${jobId} from slug_jobs table`);
    
    // Delete directly from slug_jobs table
    const { data, error } = await supabase
      .from('slug_jobs')
      .delete()
      .eq('id', jobId);
    
    if (error) {
      console.error('Error deleting job from slug_jobs table:', error);
      return { data: null, error };
    }
    
    console.log('Successfully deleted job');
    return { data: { success: true }, error: null };
  } catch (error) {
    console.error('Error deleting job:', error);
    return { data: null, error };
  }
};

export const clearTaskQueue = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('clear-queue');
    if (error) {
      console.error('Error clearing task queue:', error);
      return { success: false, error };
    }
    return { success: true, purged: data?.purged || 0, error: null };
  } catch (error) {
    console.error('Error in clearTaskQueue:', error);
    return { success: false, error };
  }
};

export const processNextTask = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('task-dispatcher');
    if (error) {
      console.error('Error processing next task:', error);
      return { success: false, error };
    }
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error in processNextTask:', error);
    return { success: false, error };
  }
};

// Helper to get a count of metrics from job result
export const getMetricsCount = (job: any) => {
  if (!job) return { totalProcessed: 0, totalUnprocessed: 0, totalErrors: 0 };
  
  const tasks = job.tasks || [];
  let totalMetrics = 0;
  let totalProcessed = 0;
  let totalUnprocessed = 0;
  let totalErrors = 0;
  
  // First check for direct metrics in the job result
  if (Array.isArray(job.result)) {
    totalMetrics += job.result.length;
  }
  
  // Then check each task
  tasks.forEach(task => {
    if (task.result) {
      // Check for processed/unprocessed counts in task result
      totalProcessed += task.result.processed || 0;
      totalUnprocessed += task.result.unprocessed || 0;
      
      // Count errors from different locations
      totalErrors += (task.result.errors?.length || 0);
      totalErrors += (task.result.errorDetails?.length || 0);
      
      // If task result has a success flag and it's false, count as an error
      if (task.result.success === false) {
        totalErrors += 1;
      }
      
      // Check for metrics in all possible locations
      const taskMetrics = getTaskMetrics(task);
      totalMetrics += taskMetrics.length;
    }
  });
  
  // If we found metrics, override the processed count
  if (totalMetrics > 0) {
    totalProcessed = totalMetrics;
  }
  
  return { totalProcessed, totalUnprocessed, totalErrors };
};

// Helper to count task statuses
export const getTaskStatusCounts = (tasks: any[]) => {
  return tasks.reduce((acc, task) => {
    acc[task.status] = (acc[task.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};

// Helper to get metrics from a task result - thorough check of all possible locations
export const getTaskMetrics = (task: any) => {
  if (!task || !task.result) return [];
  
  const result = task.result;
  
  // For PROCESS_METRIC_EVENTS jobs, extract metrics from events
  if (result.events && Array.isArray(result.events)) {
    // Try to extract metrics from fabric_json1 in events if they exist
    const metrics: any[] = [];
    
    result.events.forEach((event: any) => {
      // Added metrics
      if (event.fabric_json1?.addedMetrics && Array.isArray(event.fabric_json1.addedMetrics)) {
        event.fabric_json1.addedMetrics.forEach((metric: any) => {
          metrics.push({
            name: metric.display_name,
            example: `Added in ${event.from_version} → ${event.to_version}`,
            type: 'added'
          });
        });
      }
      
      // Removed metrics
      if (event.fabric_json1?.removedMetrics && Array.isArray(event.fabric_json1.removedMetrics)) {
        event.fabric_json1.removedMetrics.forEach((metric: any) => {
          metrics.push({
            name: metric.display_name,
            example: `Removed in ${event.from_version} → ${event.to_version}`,
            type: 'removed'
          });
        });
      }
    });
    
    if (metrics.length > 0) {
      return metrics;
    }
  }
  
  // Check for metrics in various locations based on different result formats
  if (Array.isArray(result.metrics)) {
    return result.metrics;
  }
  
  if (Array.isArray(result.processedMetrics)) {
    return result.processedMetrics;
  }
  
  // If the result itself is an array of metrics objects
  if (Array.isArray(result) && result.length > 0 && result[0] && typeof result[0] === 'object' && result[0].name) {
    return result;
  }
  
  // Look for top-level metrics array
  if (result.metrics && Array.isArray(result.metrics)) {
    return result.metrics;
  }
  
  // Check for processed property containing metrics array
  if (result.processed && Array.isArray(result.processed)) {
    return result.processed;
  }
  
  return [];
};

// Get detailed errors from a task result
export const getTaskErrors = (task: any) => {
  if (!task || !task.result) return [];
  
  const result = task.result;
  let errors: any[] = [];
  
  // Check all possible error locations
  if (result.errors && Array.isArray(result.errors)) {
    errors = [...errors, ...result.errors];
  }
  
  if (result.errorDetails && Array.isArray(result.errorDetails)) {
    errors = [...errors, ...result.errorDetails];
  }
  
  // If there's a top-level error message
  if (result.error && typeof result.error === 'string') {
    errors.push(result.error);
  }
  
  // Handle edge case where result might be an error object itself
  if (typeof result === 'object' && result.message && result.success === false) {
    errors.push(result.message);
  }
  
  return errors;
};

// Get the last successful run for a specific job type and slug
export const getLastSuccessfulRun = async (slug: string, jobType: string) => {
  try {
    const { data, error } = await supabase
      .from('slug_jobs')
      .select('*')
      .eq('slug', slug)
      .eq('job_type', jobType)
      .eq('status', 'completed')
      .order('last_successful_timestamp', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (error) {
      console.error('Error fetching last successful run:', error);
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error in getLastSuccessfulRun:', error);
    return { data: null, error };
  }
};
